// Docs: IMPLEMENTATION.md §3.2 (wake gate), FEATURES.md "App structure".
// Full-screen wake screen shown instead of stale data. It goes up on a cold
// launch whose persisted cache is older than STALE_AFTER_MS (or missing), and
// when the app comes back after being hidden that long — both mean the Railway
// api has likely gone to sleep and whatever is on screen is out of date. The
// app keeps rendering underneath (queries refetch, lesson state survives); the
// gate lifts once /health answers and the visible queries have settled — unless
// the player has started the der/die/das game (WakeGame.jsx), in which case it
// switches to a "ready" state and waits for "Weiter zur App" instead of
// yanking the game away mid-answer. Thresholds live in utils/serverWake.js.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsRestoring, useQueryClient } from '@tanstack/react-query';
import {
  STALE_AFTER_MS,
  WAKE_THRESHOLDS,
  isStale,
  persistedDataUpdatedAt,
  waitForBackend,
  waitForQueriesIdle,
} from '../utils/serverWake';
import { formatInstantRelative } from '../utils/datetime';
import WakeGame, { ARTICLES } from './WakeGame';

// Immutable content that stays valid across a sleep; everything else that
// is not on screen is dropped once the gate lifts so it refetches (with a
// skeleton) instead of flashing old numbers on the next page.
const KEEP_ACROSS_WAKE = new Set(['enums', 'languages', 'questionAudio', 'podcasts']);

const AUTH_PATHS = new Set(['/login', '/oauth/consent']);

const hasToken = () => {
  try {
    return Boolean(window.localStorage.getItem('jwt'));
  } catch {
    return false;
  }
};

function initialGate() {
  if (!hasToken() || AUTH_PATHS.has(window.location.pathname)) return null;
  const lastDataAt = persistedDataUpdatedAt();
  if (!isStale(lastDataAt)) return null;
  return { startedAt: Date.now(), lastDataAt };
}

const COPY = {
  loading: {
    title: 'Einen Moment …',
    body: 'Dein aktueller Stand wird geladen.',
  },
  sleeping: {
    title: 'Der Server wacht auf',
    body: 'Nach einer längeren Pause schläft er ein. Das Aufwachen dauert meist 5–10 Sekunden.',
  },
  syncing: {
    title: 'Server ist wach',
    body: 'Deine Daten werden aktualisiert …',
  },
  slow: {
    title: 'Dauert länger als sonst',
    body: 'Der Server braucht heute etwas länger. Wir versuchen es weiter …',
  },
  failed: {
    title: 'Server nicht erreichbar',
    body: 'Prüfe deine Verbindung und versuche es erneut.',
  },
  ready: {
    title: 'Alles bereit!',
    body: 'Der Server ist wach und deine Daten sind aktuell.',
  },
};

// Stages from which the game is on screen (it stays once shown). 'syncing'
// is not one: a warm server that is only settling queries never starts it.
const GAME_STAGES = new Set(['sleeping', 'slow', 'failed', 'ready']);

// Estimated progress: eases towards 92% around a typical 5–10s cold start,
// jumps to 100% when ready. An honest-ish bar beats an endless sweep.
const progressFor = (stage, elapsed) => {
  if (stage === 'ready') return 100;
  return 92 * (1 - Math.exp(-elapsed / 6_000));
};

// `awake`: /health has answered, only the visible queries are still loading —
// never report a sleeping server then, however long the refetch takes.
function stageFor(phase, elapsed, awake) {
  if (phase === 'failed') return 'failed';
  if (phase === 'ready') return 'ready';
  if (elapsed < WAKE_THRESHOLDS.quiet) return 'quiet';
  if (awake) return 'syncing';
  if (elapsed < WAKE_THRESHOLDS.sleeping) return 'loading';
  if (elapsed < WAKE_THRESHOLDS.slow) return 'sleeping';
  return 'slow';
}

// der / die / das in their article colours and shapes — square, circle,
// triangle (as GenusShape) — as
// three little sleepers. While the server boots they doze (eyes shut, slow
// breathing, a few floating z's); the moment /health answers they stretch,
// open their eyes and hop. Tapping one wakes it early with a spin.
const EYES = {
  circle: { y: 12, x: [8.5, 15.5] },
  triangle: { y: 16, x: [9.6, 14.4] },
  square: { y: 11.5, x: [8.5, 15.5] },
};

const Sleeper = ({ shape, open }) => {
  const { x, y } = EYES[shape];
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {shape === 'circle' && <circle cx="12" cy="12" r="10" />}
      {shape === 'triangle' && <polygon points="12,2 22,22 2,22" />}
      {shape === 'square' && <rect x="2" y="2" width="20" height="20" />}
      <g className="wake-gate__eyes">
        {x.map((cx) => (open
          ? <circle key={cx} cx={cx} cy={y} r="1.35" />
          : <path key={cx} d={`M${cx - 1.6} ${y - 0.4} q1.6 1.6 3.2 0`} />))}
      </g>
    </svg>
  );
};

const WakeMark = ({ awake }) => {
  const [poked, setPoked] = useState({});
  const poke = (article) => {
    setPoked((p) => ({ ...p, [article]: (p[article] || 0) + 1 }));
  };
  return (
    <div className="wake-gate__mark" data-mood={awake ? 'awake' : 'asleep'} aria-hidden="true">
      {ARTICLES.map(({ article, shape }) => (
        <button
          key={`${article}-${poked[article] || 0}`}
          type="button"
          tabIndex={-1}
          className={`wake-gate__shape wake-gate__shape--${article} ${poked[article] ? 'is-poked' : ''}`}
          onClick={() => poke(article)}
        >
          <Sleeper shape={shape} open={awake || Boolean(poked[article])} />
        </button>
      ))}
      {!awake && (
        <span className="wake-gate__zzz">
          <span>z</span><span>z</span><span>Z</span>
        </span>
      )}
    </div>
  );
};

// Big green check that replaces the shapes once everything is ready — the
// player's eyes are on the game below, so the switch has to be unmissable.
const ReadyMark = () => (
  <div className="wake-gate__ready-mark" aria-hidden="true">
    <svg viewBox="0 0 24 24">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  </div>
);

export default function WakeGate() {
  const queryClient = useQueryClient();
  const isRestoring = useIsRestoring();
  const location = useLocation();
  const isAuthPage = AUTH_PATHS.has(location.pathname);

  const [gate, setGate] = useState(initialGate);
  const [phase, setPhase] = useState('waiting'); // 'waiting' | 'failed' | 'ready'
  const [awake, setAwake] = useState(false); // /health answered this round
  const [now, setNow] = useState(() => Date.now());
  const [gameShown, setGameShown] = useState(false);
  // Set on the first answer; read when the backend is ready to decide
  // between lifting and waiting for "Weiter zur App".
  const playedRef = useRef(false);
  const onPlay = useCallback(() => { playedRef.current = true; }, []);

  // Back from the background after long enough for the server to sleep.
  // Skipped mid-lesson: the runner holds its own state and the chrome is
  // hidden, so a wake screen there would only get in the way.
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt == null) return;
      const away = Date.now() - hiddenAt;
      hiddenAt = null;
      if (away < STALE_AFTER_MS || !hasToken()) return;
      if (AUTH_PATHS.has(window.location.pathname)) return;
      if (document.documentElement.hasAttribute('data-lesson-active')) return;
      setPhase('waiting');
      setGate((g) => g ?? { startedAt: Date.now(), lastDataAt: null });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // A 401 during the wait lands on /login — nothing to gate there.
  useEffect(() => {
    if (isAuthPage && gate) setGate(null);
  }, [isAuthPage, gate]);

  useEffect(() => {
    if (!gate || phase !== 'waiting') return undefined;
    const tick = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(tick);
  }, [gate, phase]);

  useEffect(() => {
    if (!gate || isRestoring || phase !== 'waiting') return undefined;
    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      // Everything on screen is older than the gate: refetch it underneath.
      // cancelRefetch:false keeps fetches the restore already started.
      queryClient.invalidateQueries({ refetchType: 'active' }, { cancelRefetch: false });
      const ok = await waitForBackend({
        deadline: gate.startedAt + WAKE_THRESHOLDS.giveUp,
        signal,
      });
      if (signal.aborted) return;
      if (!ok) {
        setPhase('failed');
        return;
      }
      setAwake(true);
      // Anything that failed while the container was booting gets another go.
      await queryClient
        .refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' })
        .catch(() => {});
      await waitForQueriesIdle(queryClient);
      if (signal.aborted) return;
      queryClient.removeQueries({
        type: 'inactive',
        predicate: (q) =>
          !KEEP_ACROSS_WAKE.has(q.queryKey?.[0]) && q.state.dataUpdatedAt < gate.startedAt,
      });
      if (playedRef.current) setPhase('ready');
      else setGate(null);
    })();

    return () => controller.abort();
  }, [gate, isRestoring, phase, queryClient]);

  const stage = gate ? stageFor(phase, now - gate.startedAt, awake) : null;

  useEffect(() => {
    if (GAME_STAGES.has(stage)) setGameShown(true);
  }, [stage]);

  // Enter / Escape continue once everything is ready.
  useEffect(() => {
    if (stage !== 'ready') return undefined;
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === 'Escape') setGate(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage]);

  // Reset for the next time the gate goes up (e.g. after a long background).
  useEffect(() => {
    if (gate) return;
    playedRef.current = false;
    setGameShown(false);
    setAwake(false);
    setPhase('waiting');
  }, [gate]);

  if (!gate || isAuthPage) return null;

  const elapsed = now - gate.startedAt;
  const seconds = Math.max(0, Math.floor(elapsed / 1000));
  const hasCachedData =
    phase === 'failed' &&
    queryClient.getQueryCache().getAll().some((q) => q.state.data !== undefined);

  const retry = () => {
    setNow(Date.now());
    setAwake(false);
    setPhase('waiting');
    setGate((g) => ({ ...g, startedAt: Date.now() }));
  };

  const showGame = gameShown || GAME_STAGES.has(stage);

  return (
    <div className="wake-gate" data-stage={stage} data-testid="wake-gate">
      {stage !== 'quiet' && (
        <div className="wake-gate__panel">
          <div className="wake-gate__status">
            {stage === 'ready' ? <ReadyMark /> : <WakeMark awake={stage === 'syncing'} />}
            {/* Only the stage copy is live — not the ticking counter. */}
            <div role="status" aria-live="polite">
              <h1 className="wake-gate__title">{COPY[stage].title}</h1>
              <p className="wake-gate__body">{COPY[stage].body}</p>
            </div>

            {stage !== 'failed' && stage !== 'ready' && (
              <div className="wake-gate__progress">
                <div className="wake-gate__bar" aria-hidden="true">
                  <span style={{ width: `${progressFor(stage, elapsed)}%` }} />
                </div>
                {(stage === 'sleeping' || stage === 'slow') && (
                  <span className="wake-gate__meta">{seconds} s</span>
                )}
              </div>
            )}

            {stage === 'ready' && (
              <div className="wake-gate__actions">
                <button
                  type="button"
                  className="btn btn-primary wake-gate__continue"
                  onClick={() => setGate(null)}
                  autoFocus
                >
                  Weiter zur App →
                </button>
              </div>
            )}

            {stage === 'failed' && (
              <>
                {hasCachedData && gate.lastDataAt && (
                  <p className="wake-gate__meta">
                    Letzter gespeicherter Stand: {formatInstantRelative(new Date(gate.lastDataAt))}
                  </p>
                )}
                <div className="wake-gate__actions">
                  <button type="button" className="btn btn-primary" onClick={retry}>
                    Erneut versuchen
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setGate(null)}>
                    {hasCachedData ? 'Letzten Stand anzeigen' : 'Trotzdem öffnen'}
                  </button>
                </div>
              </>
            )}
          </div>

          {showGame && (
            <div className="wake-gate__game">
              <WakeGame onPlay={onPlay} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
